'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["namespace", "inlineToolbarContainer", "useAnimatedToolbar", "hasFocus", "forwardRef", "showToolbarShepherd"];
import PropTypes from 'prop-types';
import { useEffect, useRef } from 'react';
import classnames from 'classnames';
import TinymceToolbar from './TinymceToolbar';
import styled, { keyframes } from 'styled-components';
import { rgba } from 'UIComponents/core/Color';
import { PANTERA } from 'HubStyleTokens/colors';
import { jsx as _jsx } from "react/jsx-runtime";
const slideOut = keyframes(["0%{transform:translate3d(0,0,0);box-shadow:0 1px 5px ", ";}100%{transform:translate3d(0,-100%,0);box-shadow:0 1px 5px ", "}"], rgba(PANTERA, 0.33), rgba(PANTERA, 0));
const slideIn = keyframes(["0%{transform:translate3d(0,-100%,0);box-shadow:0 1px 5px ", ";}100%{transform:translate3d(0,0,0);box-shadow:0 1px 5px ", ";}"], rgba(PANTERA, 0), rgba(PANTERA, 0.33));
const StaticStyledToolbar = styled(TinymceToolbar).withConfig({
  displayName: "InlineToolbar__StaticStyledToolbar",
  componentId: "vxaaa6-0"
})(["box-shadow:0 1px 5px ", ";"], rgba(PANTERA, 0.15));
const AnimatedStyledToolbar = styled(TinymceToolbar).withConfig({
  displayName: "InlineToolbar__AnimatedStyledToolbar",
  componentId: "vxaaa6-1"
})(["animation-name:", ";animation-duration:", ";animation-timing-function:", ";animation-fill-mode:forwards;"], props => props.hasFocus ? slideIn : slideOut, props => props.hasFocus ? '.375s' : '.25s', props => props.hasFocus ? 'ease-out' : 'ease-in');
export default function InlineToolbar(props) {
  const {
      namespace,
      inlineToolbarContainer,
      useAnimatedToolbar,
      hasFocus,
      forwardRef,
      showToolbarShepherd
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const StyledToolbar = useAnimatedToolbar ? AnimatedStyledToolbar : StaticStyledToolbar;
  const editor = props.editor;
  const className = classnames('mce-toolbar', namespace);
  const styledToolbar = useRef();
  useEffect(() => {
    const toolbar = styledToolbar.current;
    if (hasFocus) {
      toolbar.style.display = 'flex';
    }
    const handleAnimationEnd = () => {
      if (!hasFocus) {
        toolbar.style.display = 'none';
      }
      editor.fire('ToolbarAnimationEnd', {
        hasFocus
      });
    };
    toolbar.addEventListener('animationend', handleAnimationEnd);
    return () => {
      toolbar.removeEventListener('animationend', handleAnimationEnd);
    };
  }, [editor, inlineToolbarContainer, hasFocus]);
  useEffect(() => {
    editor.inlineToolbarContainer = inlineToolbarContainer;
    return () => {
      editor.inlineToolbarContainer = null;
    };
  }, [editor, inlineToolbarContainer]);
  return /*#__PURE__*/_jsx("div", {
    tabIndex: "-1",
    role: "application",
    className: className,
    children: /*#__PURE__*/_jsx(StyledToolbar, Object.assign({}, rest, {
      forwardRef: el => {
        if (forwardRef) {
          forwardRef(el);
        }
        styledToolbar.current = el;
      },
      hasFocus: hasFocus,
      centerControls: true,
      showToolbarShepherd: showToolbarShepherd && hasFocus
    }))
  });
}
InlineToolbar.propTypes = {
  inlineToolbarContainer: PropTypes.instanceOf(Element).isRequired,
  useAnimatedToolbar: PropTypes.bool,
  editor: PropTypes.object.isRequired,
  hasFocus: PropTypes.bool.isRequired,
  namespace: PropTypes.string,
  forwardRef: PropTypes.func,
  showToolbarShepherd: PropTypes.bool
};
InlineToolbar.defaultProps = {
  useAnimatedToolbar: false
};